import { z } from 'zod';
import { SEARCH_QUERY_MAX } from './catalog';
import { documentIdSchema } from './documents';

/** Open Library work ids, e.g. OL3140822W. */
export const workIdSchema = z.string().regex(/^OL[1-9]\d{0,9}W$/, 'Unknown Open Library work');
export const workParamsSchema = z.object({ workId: workIdSchema });

/** Internet Archive item identifiers, e.g. greatgatsby0000fsco. They end up in a frame's address. */
export const iaIdSchema = z
  .string()
  .regex(/^[A-Za-z0-9][A-Za-z0-9._-]{0,99}$/, 'Unknown Internet Archive item');

/**
 * How a book from the wider catalogue can be read:
 * - `libellus`: the full text is read in Libellus, with progress and bookmarks: a Project
 *   Gutenberg text, or the recognised text of a free Internet Archive scan;
 * - `none`: listed only: no copy Libellus can show in full (lent books are never offered).
 */
export const bookAccessSchema = z.enum(['libellus', 'none']);
export type BookAccess = z.infer<typeof bookAccessSchema>;

export const openLibraryBookSchema = z.object({
  workId: workIdSchema,
  title: z.string(),
  /** Tells a novel from the study guides that share its title. */
  subtitle: z.string().nullable(),
  authors: z.array(z.string()),
  year: z.number().int().nullable(),
  coverUrl: z.string().nullable(),
  editionCount: z.number().int().min(0),
  access: bookAccessSchema,
  /** Where to read it in Libellus, when `access` is `libellus`. */
  documentId: documentIdSchema.nullable(),
  /** The free English scan, whose page images Internet Archive's viewer shows. */
  iaId: iaIdSchema.nullable(),
  /** The same scan on Internet Archive's own site. */
  readUrl: z.string().nullable(),
  openLibraryUrl: z.string(),
});
export type OpenLibraryBook = z.infer<typeof openLibraryBookSchema>;

export const openLibraryWorkSchema = openLibraryBookSchema.extend({
  description: z.string().nullable(),
  subjects: z.array(z.string()),
});
export type OpenLibraryWork = z.infer<typeof openLibraryWorkSchema>;

export const openLibrarySortSchema = z.enum(['relevance', 'newest', 'oldest', 'title']);
export type OpenLibrarySort = z.infer<typeof openLibrarySortSchema>;

export const openLibrarySearchQuerySchema = z.object({
  q: z.string().trim().max(SEARCH_QUERY_MAX).default(''),
  sort: openLibrarySortSchema.default('relevance'),
  cursor: z.string().max(20).optional(),
});
export type OpenLibrarySearchQuery = z.infer<typeof openLibrarySearchQuerySchema>;

export const openLibrarySearchPageSchema = z.object({
  items: z.array(openLibraryBookSchema),
  total: z.number().int().min(0),
  nextCursor: z.string().nullable(),
});
export type OpenLibrarySearchPage = z.infer<typeof openLibrarySearchPageSchema>;
